import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Camera, Upload, Search, Loader2, Eye, Package, X, FileText,
  Lightbulb, Building, Star, CheckCircle, History, Ban
} from 'lucide-react';
import api from '../lib/api';

const t = (lang, pl, en) => lang === 'pl' ? pl : en;

export default function VisualMatcher({ lang, tenantId }) {
  const [image, setImage] = useState(null);        // File object
  const [preview, setPreview] = useState(null);     // base64 preview
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [pdfResult, setPdfResult] = useState(null); // PDF multi-match result
  const [mode, setMode] = useState('image');         // 'image' or 'pdf'
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  const batchRef = useRef(null);
  const [batchFiles, setBatchFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submittingBatch, setSubmittingBatch] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      const response = await api.get('/products/visual-jobs', { params: { limit: 20 } });
      setJobs(response.data.items || []);
    } catch (error) {
      // History is secondary; do not interrupt an active analysis with a toast.
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    if (!jobs.some((job) => ['queued', 'running'].includes(job.status))) return undefined;
    const timer = setInterval(loadJobs, 2000);
    return () => clearInterval(timer);
  }, [jobs, loadJobs]);

  function selectBatchFiles(fileList) {
    const selected = Array.from(fileList || []);
    const valid = selected.filter((file) => file.type.startsWith('image/') || file.type === 'application/pdf');
    const totalBytes = valid.reduce((sum, file) => sum + file.size, 0);
    if (valid.length !== selected.length) {
      toast.error(t(lang, 'Dozwolone są tylko obrazy i PDF.', 'Only images and PDFs are allowed.'));
      return;
    }
    if (!valid.length || valid.length > 20 || totalBytes > 100 * 1024 * 1024 || valid.some((file) => file.size > 20 * 1024 * 1024)) {
      toast.error(t(lang, 'Limit: 20 plików, 20MB/plik i 100MB łącznie.', 'Limit: 20 files, 20MB/file and 100MB total.'));
      return;
    }
    setBatchFiles(valid);
  }

  async function submitBatch() {
    if (!batchFiles.length) return;
    setSubmittingBatch(true);
    setUploadProgress(0);
    const form = new FormData();
    batchFiles.forEach((file) => form.append('files', file));
    try {
      await api.post('/products/visual-jobs', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
        onUploadProgress: (event) => setUploadProgress(event.total ? Math.round(event.loaded * 100 / event.total) : 0),
      });
      setBatchFiles([]);
      if (batchRef.current) batchRef.current.value = '';
      await loadJobs();
      toast.success(t(lang, 'Zadanie dodane do kolejki.', 'Job added to the queue.'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t(lang, 'Nie udało się utworzyć zadania.', 'Could not create the job.'));
    } finally {
      setSubmittingBatch(false);
    }
  }

  async function cancelJob(jobId) {
    try {
      await api.post(`/products/visual-jobs/${jobId}/cancel`);
      await loadJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || t(lang, 'Nie można anulować zadania.', 'Cannot cancel job.'));
    }
  }

  function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t(lang, 'Tylko obrazy (PNG, JPG)', 'Images only (PNG, JPG)'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t(lang, 'Max 10MB', 'Max 10MB'));
      return;
    }
    setImage(file);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  async function handleMatch() {
    if (!image) return;
    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('image', image);

      const res = await api.post('/products/visual-match', formData, {
        params: { tenant_id: tenantId },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });

      setResult(res.data);

      if (res.data.total_found > 0) {
        toast.success(t(lang,
          `Znaleziono ${res.data.total_found} pasujących produktów`,
          `Found ${res.data.total_found} matching products`
        ));
      } else {
        toast.info(t(lang,
          'Nie znaleziono dokładnych dopasowań — sprawdź opis AI poniżej',
          'No exact matches — check AI description below'
        ));
      }
    } catch (err) {
      toast.error(t(lang, 'Błąd analizy: ' + (err.message || 'spróbuj ponownie'), 'Analysis error'));
    }
    setLoading(false);
  }

  async function handlePdfMatch() {
    if (!image) return;
    setLoading(true);
    setPdfResult(null);

    try {
      const formData = new FormData();
      formData.append('file', image);

      const res = await api.post('/products/visual-match-pdf', formData, {
        params: { tenant_id: tenantId },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // PDF analysis takes longer
      });

      setPdfResult(res.data);

      if (res.data.total_unique > 0) {
        toast.success(t(lang,
          `Zidentyfikowano ${res.data.total_unique} produktów z ${res.data.images_found} obrazów`,
          `Identified ${res.data.total_unique} products from ${res.data.images_found} images`
        ));
      } else {
        toast.info(t(lang, 'Nie znaleziono produktów w PDF', 'No products found in PDF'));
      }
    } catch (err) {
      toast.error(t(lang, 'Błąd analizy PDF', 'PDF analysis error'));
    }
    setLoading(false);
  }

  function clearImage() {
    setImage(null);
    setPreview(null);
    setResult(null);
    setPdfResult(null);
    setMode('image');
    if (fileRef.current) fileRef.current.value = '';
    if (pdfRef.current) pdfRef.current.value = '';
  }

  function handleFileAny(file) {
    if (!file) return;
    if (file.type === 'application/pdf') {
      setMode('pdf');
      setImage(file);
      setPreview(null);
      setResult(null);
      setPdfResult(null);
    } else if (file.type.startsWith('image/')) {
      setMode('image');
      handleFile(file);
    } else {
      toast.error(t(lang, 'Obsługiwane formaty: PNG, JPG, PDF', 'Supported: PNG, JPG, PDF'));
    }
  }

  const vision = result?.vision_analysis;
  const matches = result?.matches || [];
  const marketplaceMatches = result?.marketplace_matches || [];
  const pdfProducts = pdfResult?.products_identified || [];

  const confidenceColor = (c) => {
    if (c >= 0.7) return 'bg-emerald-500/20 text-emerald-400 border-emerald-400/30';
    if (c >= 0.5) return 'bg-amber-500/20 text-amber-400 border-amber-400/30';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
          {t(lang, 'Rozpoznawanie produktów ze zdjęć', 'Visual Product Recognition')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t(lang,
            'Wrzuć zdjęcie produktu (np. lampa, mebel, część zamienna) — AI zidentyfikuje go w katalogu',
            'Upload a product photo (e.g. lamp, furniture, spare part) — AI will identify it in the catalog'
          )}
        </p>
      </div>

      <Card className="border-primary/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4" />{t(lang, 'Analiza grupowa', 'Batch analysis')}</CardTitle>
          <CardDescription>{t(lang, 'Do 20 zdjęć i wielu PDF-ów; zadanie działa w tle i zachowuje historię.', 'Up to 20 images and multiple PDFs; jobs run in the background and retain history.')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <input ref={batchRef} type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf"
              onChange={(event) => selectBatchFiles(event.target.files)} className="hidden" />
            <Button variant="outline" onClick={() => batchRef.current?.click()} disabled={submittingBatch}>
              <Upload className="w-4 h-4 mr-2" />{t(lang, 'Wybierz pliki', 'Choose files')}
            </Button>
            <Button onClick={submitBatch} disabled={!batchFiles.length || submittingBatch}>
              {submittingBatch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {t(lang, `Uruchom (${batchFiles.length})`, `Start (${batchFiles.length})`)}
            </Button>
          </div>
          {batchFiles.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {batchFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className="flex items-center justify-between rounded border p-2 text-xs">
                  <span className="truncate mr-2">{file.name}</span>
                  <button onClick={() => setBatchFiles((items) => items.filter((_, i) => i !== index))}><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
          {submittingBatch && <Progress value={uploadProgress} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" />{t(lang, 'Kolejka i historia', 'Queue and history')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!jobs.length && <p className="text-sm text-muted-foreground">{t(lang, 'Brak zadań.', 'No jobs yet.')}</p>}
          {jobs.map((job) => {
            const offers = (job.results || []).flatMap((item) => item.result?.marketplace_matches || []);
            return (
              <div key={job.id} className="rounded-lg border p-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{job.total_files} {t(lang, 'plików', 'files')}</div>
                    <div className="text-xs text-muted-foreground truncate">{(job.files || []).map((file) => file.filename).join(', ')}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{job.status}</Badge>
                    {['queued', 'running'].includes(job.status) && (
                      <Button size="sm" variant="destructive" onClick={() => cancelJob(job.id)}><Ban className="w-3 h-3 mr-1" />{t(lang, 'Anuluj', 'Cancel')}</Button>
                    )}
                  </div>
                </div>
                <Progress value={job.progress_pct || 0} />
                <div className="text-xs text-muted-foreground">{job.completed_files || 0}/{job.total_files} · {job.progress_pct || 0}%</div>
                {offers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {offers.slice(0, 8).map((offer, index) => (
                      <a key={`${offer.offer_url}-${index}`} href={offer.offer_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:underline">{offer.marketplace}: {offer.title?.slice(0, 45)}</a>
                    ))}
                  </div>
                )}
                {(job.errors || []).map((error) => <p key={error.file_id} className="text-xs text-red-400">{error.filename}: {error.error}</p>)}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Upload area */}
      <Card className="border-2 border-dashed border-border hover:border-primary/50 transition-colors">
        <CardContent className="p-8">
          {!preview && mode !== 'pdf' ? (
            <div
              className="text-center cursor-pointer"
              onDrop={(e) => { e.preventDefault(); handleFileAny(e.dataTransfer?.files?.[0]); }}
              onDragOver={handleDragOver}
              onClick={() => fileRef.current?.click()}
            >
              <Camera className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {t(lang, 'Przeciągnij zdjęcie produktu lub PDF z ofertą/katalogiem', 'Drag a product photo or an offer/catalog PDF')}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t(lang, 'Zdjęcie (PNG, JPG) lub PDF z ofertą — max 20MB', 'Image (PNG, JPG) or offer PDF — max 20MB')}
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" className="gap-2" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                  <Camera className="w-4 h-4" />
                  {t(lang, 'Zdjęcie', 'Photo')}
                </Button>
                <Button variant="outline" className="gap-2" onClick={(e) => { e.stopPropagation(); pdfRef.current?.click(); }}>
                  <FileText className="w-4 h-4" />
                  {t(lang, 'PDF oferty', 'Offer PDF')}
                </Button>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <input
                ref={pdfRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => handleFileAny(e.target.files?.[0])}
              />
            </div>
          ) : mode === 'pdf' && image ? (
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-64 h-48 bg-muted rounded-lg flex items-center justify-center relative">
                <FileText className="w-16 h-16 text-muted-foreground/50" />
                <button onClick={clearImage} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <p className="text-sm font-medium">{image.name}</p>
                  <p className="text-xs text-muted-foreground">{(image.size / 1024).toFixed(0)} KB — PDF</p>
                </div>
                <Button onClick={handlePdfMatch} disabled={loading} className="gap-2 w-full max-w-xs" size="lg">
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />{t(lang, 'Analizuję PDF...', 'Analyzing PDF...')}</>
                  ) : (
                    <><Search className="w-4 h-4" />{t(lang, 'Rozpoznaj produkty z PDF', 'Identify products from PDF')}</>
                  )}
                </Button>
                {loading && (
                  <p className="text-xs text-muted-foreground">
                    {t(lang,
                      'Wyciągam obrazy z PDF → analizuję każdy produkt → szukam w katalogu 49,000+ produktów...',
                      'Extracting images from PDF → analyzing each product → searching 49,000+ products...'
                    )}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex gap-6 items-start">
              {/* Image preview */}
              <div className="relative flex-shrink-0">
                <img
                  src={preview}
                  alt="Upload"
                  className="w-64 h-64 object-contain rounded-lg bg-muted"
                />
                <button
                  onClick={clearImage}
                  className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Action */}
              <div className="flex-1 space-y-4">
                <div>
                  <p className="text-sm font-medium">{image.name}</p>
                  <p className="text-xs text-muted-foreground">{(image.size / 1024).toFixed(0)} KB</p>
                </div>
                <Button
                  onClick={handleMatch}
                  disabled={loading}
                  className="gap-2 w-full max-w-xs"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t(lang, 'Analizuję...', 'Analyzing...')}
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      {t(lang, 'Rozpoznaj produkt', 'Identify product')}
                    </>
                  )}
                </Button>
                {loading && (
                  <p className="text-xs text-muted-foreground">
                    {t(lang,
                      'GPT-4o analizuje zdjęcie i szuka w katalogu 49,000+ produktów...',
                      'GPT-4o analyzing image and searching 49,000+ products...'
                    )}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading skeleton */}
      {loading && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {/* Vision Analysis Result */}
      {vision && !vision.error && (
        <Card className="border border-primary/20 bg-primary/5">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-primary mb-2">
                  {t(lang, 'Analiza AI', 'AI Analysis')}
                </h3>
                {vision.description_pl && (
                  <p className="text-sm mb-3">{vision.description_pl}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {vision.fixture_type && vision.fixture_type !== 'unknown' && (
                    <Badge variant="secondary">{vision.fixture_type}</Badge>
                  )}
                  {vision.style && <Badge variant="outline">{vision.style}</Badge>}
                  {vision.color && <Badge variant="outline">{vision.color}</Badge>}
                  {vision.mounting && <Badge variant="outline">{vision.mounting}</Badge>}
                  {vision.manufacturer_guess && vision.manufacturer_guess !== 'unknown' && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-400/30">
                      <Building className="w-3 h-3 mr-1" />
                      {vision.manufacturer_guess}
                    </Badge>
                  )}
                  {vision.model_guess && vision.model_guess !== 'unknown' && (
                    <Badge className="bg-blue-500/20 text-blue-400">
                      {vision.model_guess}
                    </Badge>
                  )}
                </div>
              </div>
              <Badge variant="outline" className={confidenceColor(vision.confidence || 0)}>
                {Math.round((vision.confidence || 0) * 100)}% {t(lang, 'pewności', 'confidence')}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Matched Products */}
      {matches.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
            <Package className="w-4 h-4" />
            {t(lang, `Znaleziono ${matches.length} pasujących produktów`, `Found ${matches.length} matching products`)}
          </h3>
          {matches.map((m, i) => (
            <Card key={i} className={`border transition-colors ${i === 0 ? 'border-primary/30 bg-primary/5' : 'border-border hover:border-border/80'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {i === 0 && <Star className="w-4 h-4 text-primary flex-shrink-0" />}
                      <h4 className="text-sm font-semibold truncate">{m.name}</h4>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {m.manufacturer && <Badge variant="secondary" className="text-xs">{m.manufacturer}</Badge>}
                      {m.category && <Badge variant="outline" className="text-xs">{m.category}</Badge>}
                      {m.sku && <Badge variant="outline" className="text-xs font-mono">SKU: {m.sku}</Badge>}
                    </div>
                    {m.specifications && Object.keys(m.specifications).length > 0 && (
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {m.specifications.power > 0 && <span>{m.specifications.power}W</span>}
                        {m.specifications.lumens > 0 && <span>{m.specifications.lumens}lm</span>}
                        {m.specifications.cct_k > 0 && <span>{m.specifications.cct_k}K</span>}
                        {m.specifications.ip_rating && <span>{m.specifications.ip_rating}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 ml-3 flex-shrink-0">
                    <Badge variant="outline" className={confidenceColor(m.match_confidence)}>
                      {Math.round(m.match_confidence * 100)}%
                    </Badge>
                    <span className="text-xs text-muted-foreground">{m.match_method}</span>
                    {m.product_url && (
                      <a href={m.product_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {t(lang, 'Strona', 'Page')}
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Chinese marketplace discovery results */}
      {result && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
            <Search className="w-4 h-4" />
            {t(lang, 'Chińskie platformy i dostawcy', 'Chinese marketplaces and suppliers')}
          </h3>
          {marketplaceMatches.length > 0 ? marketplaceMatches.map((offer, i) => (
            <Card key={`${offer.offer_url}-${i}`} className="border border-border">
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary">{offer.marketplace}</Badge>
                    <span className="text-xs text-amber-400">
                      {t(lang, 'wymaga weryfikacji', 'verification required')}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold">{offer.title}</h4>
                  {offer.snippet && <p className="text-xs text-muted-foreground mt-1">{offer.snippet}</p>}
                </div>
                <a href={offer.offer_url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 flex-shrink-0">
                  <Eye className="w-3 h-3" />{t(lang, 'Oferta', 'Offer')}
                </a>
              </CardContent>
            </Card>
          )) : (
            <Card className="border border-border">
              <CardContent className="p-4 text-xs text-muted-foreground">
                {result.marketplace_search?.status === 'unavailable'
                  ? t(lang, 'Wyszukiwanie internetowe nie jest skonfigurowane (brak SERPER_API_KEY).', 'Internet search is not configured (SERPER_API_KEY missing).')
                  : t(lang, 'Nie znaleziono ofert na obsługiwanych platformach.', 'No offers found on supported marketplaces.')}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* PDF Results */}
      {pdfResult && (
        <div className="space-y-4">
          {/* Summary */}
          <Card className="border border-primary/20 bg-primary/5">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-primary mb-1">
                    {t(lang, 'Analiza PDF', 'PDF Analysis')}
                  </h3>
                  <p className="text-sm">{pdfResult.summary}</p>
                  {pdfResult.manufacturers_detected?.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {pdfResult.manufacturers_detected.map(m => (
                        <Badge key={m} className="bg-emerald-500/20 text-emerald-400">
                          <Building className="w-3 h-3 mr-1" />{m}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Products from PDF */}
          {pdfProducts.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                <Package className="w-4 h-4" />
                {t(lang, `${pdfProducts.length} zidentyfikowanych produktów`, `${pdfProducts.length} identified products`)}
              </h3>
              {pdfProducts.map((p, i) => (
                <Card key={i} className="border border-border hover:border-border/80 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            {t(lang, 'Str.', 'P.')} {p.page}
                          </Badge>
                          <h4 className="text-sm font-semibold truncate">{p.product.name}</h4>
                        </div>
                        {p.vision_analysis?.description_pl && (
                          <p className="text-xs text-muted-foreground mb-2 italic">
                            AI: {p.vision_analysis.description_pl}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mb-1">
                          {p.product.manufacturer && <Badge variant="secondary" className="text-xs">{p.product.manufacturer}</Badge>}
                          {p.product.category && <Badge variant="outline" className="text-xs">{p.product.category}</Badge>}
                          {p.product.sku && <Badge variant="outline" className="text-xs font-mono">SKU: {p.product.sku}</Badge>}
                        </div>
                        {p.product.specifications && Object.keys(p.product.specifications).length > 0 && (
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            {p.product.specifications.power > 0 && <span>{p.product.specifications.power}W</span>}
                            {p.product.specifications.lumens > 0 && <span>{p.product.specifications.lumens}lm</span>}
                            {p.product.specifications.cct_k > 0 && <span>{p.product.specifications.cct_k}K</span>}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-3 flex-shrink-0">
                        <Badge variant="outline" className={confidenceColor(p.match_confidence)}>
                          {Math.round(p.match_confidence * 100)}%
                        </Badge>
                        {p.product.product_url && (
                          <a href={p.product.product_url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                            <Eye className="w-3 h-3" />{t(lang, 'Strona', 'Page')}
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No results */}
      {result && matches.length === 0 && !loading && (
        <Card className="border border-amber-500/20">
          <CardContent className="p-6 text-center">
            <Search className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="text-sm font-medium mb-1">
              {t(lang, 'Nie znaleziono dokładnych dopasowań', 'No exact matches found')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t(lang,
                'AI opisał produkt powyżej — możesz użyć tych informacji do ręcznego wyszukania w katalogu.',
                'AI described the product above — you can use this info to search manually.'
              )}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
